import React, { useState, useRef, useMemo, useEffect, useLayoutEffect } from "react";
import { PiMinus, PiPlus, PiCornersOut } from "react-icons/pi";

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const clampZoom = (value) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));

const DocumentCanvas = ({ doc, categories, focusRequest, activeRecommendationIndexes, onTagTokens, onOpenPicker, drawMode, manualEntities, struckStarts, uncheckedStarts, textByStart, onDrawRegion, onRemoveManual, onHighlightEntity, onHighlightZone }) => {
  const scrollRef = useRef(null);
  const innerRef = useRef(null);
  const imageRef = useRef(null);
  const dragRef = useRef(null);
  const zoomAnchorRef = useRef(null);
  const previousZoomRef = useRef(1);
  const [natural, setNatural] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [selection, setSelection] = useState(null);
  const [flashIndexes, setFlashIndexes] = useState([]);
  const [hoveredSpan, setHoveredSpan] = useState(null);

  useEffect(() => {
    setSelection(null);
    setHoveredSpan(null);

    const image = imageRef.current;
    if (image?.complete && image.naturalWidth > 0) setNatural({ width: image.naturalWidth, height: image.naturalHeight });
    else setNatural(null);
  }, [doc?._id]);

  const tokenBoxes = useMemo(() => {
    if (!doc?.polygons || !natural) return [];

    const boxes = doc.polygons.map((polygon, index) => {
      if (!polygon || polygon.length === 0) return null;
      const xs = polygon.map((point) => point.X * natural.width);
      const ys = polygon.map((point) => point.Y * natural.height);
      return { index, minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
    });

    const sameBox = (a, b) => a && b && a.minX === b.minX && a.maxX === b.maxX && a.minY === b.minY && a.maxY === b.maxY;

    let runStart = 0;
    while (runStart < boxes.length) {
      let runEnd = runStart;
      while (runEnd + 1 < boxes.length && doc.tokens[runEnd + 1] !== "\n" && sameBox(boxes[runEnd + 1], boxes[runStart])) runEnd++;

      if (runEnd > runStart && boxes[runStart] && doc.tokens[runStart] !== "\n") {
        const isPunctuation = (token) => !/[\p{L}\p{N}]/u.test(token || "");
        const weightOf = (token) => (isPunctuation(token) ? 0.5 : token?.length || 1);
        let totalWeight = 0;
        for (let i = runStart; i <= runEnd; i++) totalWeight += weightOf(doc.tokens[i]) + (i > runStart && !isPunctuation(doc.tokens[i]) ? 1 : 0);
        const { minX, maxX } = boxes[runStart];
        const width = maxX - minX;

        let cursor = 0;
        for (let i = runStart; i <= runEnd; i++) {
          if (i > runStart && !isPunctuation(doc.tokens[i])) cursor += 1;
          const weight = weightOf(doc.tokens[i]);
          boxes[i] = { ...boxes[i], minX: minX + (cursor / totalWeight) * width, maxX: minX + ((cursor + weight) / totalWeight) * width };
          cursor += weight;
        }
      }

      runStart = runEnd + 1;
    }

    return boxes;
  }, [doc, natural]);

  const buildSpans = (labelAt) => {
    const spans = [];
    for (let i = 0; i < (doc?.labels?.length || 0); i++) {
      const label = labelAt(i);
      const box = tokenBoxes[i];
      if (!label || label === "O" || !box) continue;

      const name = label.replace(/^(B-|I-)/, "");
      const previous = spans[spans.length - 1];
      if (label.startsWith("I-") && previous?.name === name && previous?.lastIndex === i - 1) {
        previous.lastIndex = i;
        previous.indexes.push(i);
        previous.minX = Math.min(previous.minX, box.minX);
        previous.minY = Math.min(previous.minY, box.minY);
        previous.maxX = Math.max(previous.maxX, box.maxX);
        previous.maxY = Math.max(previous.maxY, box.maxY);
        continue;
      }

      spans.push({ name, indexes: [i], lastIndex: i, minX: box.minX, minY: box.minY, maxX: box.maxX, maxY: box.maxY });
    }
    return spans;
  };

  const labelSpans = useMemo(() => {
    if (tokenBoxes.length === 0) return [];
    return buildSpans((i) => doc.labels[i]).map((span) => ({
      ...span,
      colorClass: categories.find((category) => category.name === span.name)?.text || "text-gray-500",
    }));
  }, [doc, tokenBoxes, categories]);

  const hoveredText = useMemo(() => {
    if (!hoveredSpan) return "";
    const override = textByStart?.get(hoveredSpan.indexes[0]);
    const text = override ?? hoveredSpan.indexes.map((index) => doc.tokens[index]).filter((token) => token !== "\n").join(" ");
    return text.length > 48 ? `${text.slice(0, 48)}…` : text;
  }, [hoveredSpan, doc, textByStart]);

  const recommendedSpans = useMemo(() => {
    if (tokenBoxes.length === 0 || !doc?.recommendedLabels) return [];

    const spans = [];
    for (let i = 0; i < doc.recommendedLabels.length; i++) {
      const label = doc.recommendedLabels[i];
      const box = tokenBoxes[i];
      if (label === null || label === undefined || !box) continue;

      const name = label === "O" ? "O" : label.replace(/^(B-|I-)/, "");
      const previous = spans[spans.length - 1];
      if (previous?.name === name && previous?.lastIndex === i - 1 && (label.startsWith("I-") || name === "O")) {
        previous.lastIndex = i;
        previous.indexes.push(i);
        previous.minX = Math.min(previous.minX, box.minX);
        previous.minY = Math.min(previous.minY, box.minY);
        previous.maxX = Math.max(previous.maxX, box.maxX);
        previous.maxY = Math.max(previous.maxY, box.maxY);
        continue;
      }

      spans.push({ name, indexes: [i], lastIndex: i, minX: box.minX, minY: box.minY, maxX: box.maxX, maxY: box.maxY });
    }
    return spans;
  }, [doc, tokenBoxes]);

  const activeIndexSet = useMemo(() => new Set(activeRecommendationIndexes || []), [activeRecommendationIndexes]);

  const manualBoxes = useMemo(() => {
    if (!natural) return [];
    return (manualEntities || []).map((entity, index) => {
      const xs = entity.polygon.map((point) => point.X * natural.width);
      const ys = entity.polygon.map((point) => point.Y * natural.height);
      return {
        index,
        label: entity.label,
        text: entity.text,
        minX: Math.min(...xs),
        maxX: Math.max(...xs),
        minY: Math.min(...ys),
        maxY: Math.max(...ys),
        colorClass: categories.find((category) => category.name === entity.label)?.text || "text-gray-500",
        struck: entity.active === false && entity.reason !== "unchecked",
        unchecked: entity.active === false && entity.reason === "unchecked",
      };
    });
  }, [manualEntities, natural, categories]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const onWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();

      const rect = container.getBoundingClientRect();
      zoomAnchorRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };

      const ratio = Math.min(1.12, Math.max(0.89, Math.exp(-event.deltaY * 0.006)));
      setZoom((previous) => clampZoom(previous * ratio));
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, []);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    const anchor = zoomAnchorRef.current;
    const previousZoom = previousZoomRef.current;
    previousZoomRef.current = zoom;
    if (!container || !anchor || previousZoom === zoom) return;

    const ratio = zoom / previousZoom;
    container.scrollLeft = (container.scrollLeft + anchor.x) * ratio - anchor.x;
    container.scrollTop = (container.scrollTop + anchor.y) * ratio - anchor.y;
    zoomAnchorRef.current = null;
  }, [zoom]);

  useEffect(() => {
    if (!focusRequest || !natural || !innerRef.current || !scrollRef.current) return;
    const box = tokenBoxes[focusRequest.index];
    if (!box) return;

    const container = scrollRef.current;
    const scale = innerRef.current.clientWidth / natural.width;

    container.scrollTo({
      left: ((box.minX + box.maxX) / 2) * scale - container.clientWidth / 2,
      top: ((box.minY + box.maxY) / 2) * scale - container.clientHeight / 2,
      behavior: "smooth",
    });

    setFlashIndexes(focusRequest.indexes);
    const timeout = setTimeout(() => setFlashIndexes([]), 1600);
    return () => clearTimeout(timeout);
  }, [focusRequest]);

  const onZoomStep = (ratio) => {
    const container = scrollRef.current;
    if (container) zoomAnchorRef.current = { x: container.clientWidth / 2, y: container.clientHeight / 2 };
    setZoom((previous) => clampZoom(previous * ratio));
  };

  const toNatural = (event) => {
    const rect = innerRef.current.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * natural.width,
      y: ((event.clientY - rect.top) / rect.height) * natural.height,
    };
  };

  const onMouseDown = (event) => {
    if (!natural || event.button !== 0) return;
    event.preventDefault();
    dragRef.current = { clientX: event.clientX, clientY: event.clientY };
    const point = toNatural(event);
    setSelection({ startX: point.x, startY: point.y, endX: point.x, endY: point.y });
  };

  const onMouseMove = (event) => {
    if (!selection) return;
    const point = toNatural(event);
    setSelection((previous) => ({ ...previous, endX: point.x, endY: point.y }));
  };

  const onMouseUp = (event) => {
    if (!selection) return;

    const isDrag = dragRef.current && (Math.abs(event.clientX - dragRef.current.clientX) > 5 || Math.abs(event.clientY - dragRef.current.clientY) > 5);
    const rect = {
      minX: Math.min(selection.startX, selection.endX),
      maxX: Math.max(selection.startX, selection.endX),
      minY: Math.min(selection.startY, selection.endY),
      maxY: Math.max(selection.startY, selection.endY),
    };

    setSelection(null);
    dragRef.current = null;

    const rectIsDrawable = rect.maxX - rect.minX >= natural.width * 0.004 && rect.maxY - rect.minY >= natural.height * 0.004;
    const rectToPolygon = () => [
      { X: rect.minX / natural.width, Y: rect.minY / natural.height },
      { X: rect.maxX / natural.width, Y: rect.minY / natural.height },
      { X: rect.maxX / natural.width, Y: rect.maxY / natural.height },
      { X: rect.minX / natural.width, Y: rect.maxY / natural.height },
    ];

    if (drawMode) {
      if (!rectIsDrawable) return;
      return onDrawRegion(rectToPolygon(), { x: event.clientX, y: event.clientY });
    }

    if (!isDrag && event.shiftKey) {
      const point = { x: rect.minX, y: rect.minY };
      const span = labelSpans.find((item) => point.x >= item.minX && point.x <= item.maxX && point.y >= item.minY && point.y <= item.maxY);
      if (span) return onHighlightEntity?.(span.indexes[0]);
      const zone = manualBoxes.find((item) => point.x >= item.minX && point.x <= item.maxX && point.y >= item.minY && point.y <= item.maxY);
      if (zone) return onHighlightZone?.(zone.index);
      return;
    }

    const indexes = tokenBoxes
      .filter((box) => box && doc.tokens[box.index] !== "\n" && rect.maxX >= box.minX && rect.minX <= box.maxX && rect.maxY >= box.minY && rect.minY <= box.maxY)
      .map((box) => box.index);

    if (indexes.length === 0) {
      if (!isDrag || !rectIsDrawable) return;
      return onDrawRegion(rectToPolygon(), { x: event.clientX, y: event.clientY });
    }
    if (!isDrag && event.altKey) return onOpenPicker({ x: event.clientX, y: event.clientY }, indexes);

    onTagTokens(indexes);
  };

  if (!doc) return null;

  return (
    <div className="relative h-full">
      <div ref={scrollRef} className="h-full overflow-auto p-6">
        <div
          ref={innerRef}
          className="relative mx-auto cursor-crosshair select-none"
          style={{ width: `${zoom * 100}%` }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          <img
            key={doc._id}
            ref={imageRef}
            src={doc.imageS3Url}
            className="w-full bg-white ring-1 ring-gray-950/10"
            draggable={false}
            onLoad={(event) => setNatural({ width: event.target.naturalWidth, height: event.target.naturalHeight })}
          />
          {natural && (
            <svg viewBox={`0 0 ${natural.width} ${natural.height}`} className="pointer-events-none absolute inset-0 h-full w-full" preserveAspectRatio="none">
              {labelSpans.map((span, index) => (
                <rect
                  key={index}
                  x={span.minX}
                  y={span.minY}
                  width={span.maxX - span.minX}
                  height={span.maxY - span.minY}
                  className={`${span.colorClass} fill-current`}
                  fillOpacity="0.25"
                  style={{ pointerEvents: "auto" }}
                  onMouseEnter={() => setHoveredSpan(span)}
                  onMouseLeave={() => setHoveredSpan(null)}
                />
              ))}
              {labelSpans
                .filter((span) => struckStarts?.has(span.indexes[0]))
                .map((span, index) => (
                  <line
                    key={`struck-${index}`}
                    x1={span.minX}
                    y1={(span.minY + span.maxY) / 2}
                    x2={span.maxX}
                    y2={(span.minY + span.maxY) / 2}
                    className="text-amber-600"
                    stroke="currentColor"
                    strokeWidth={Math.max(2, natural.width / 350)}
                  />
                ))}
              {labelSpans
                .filter((span) => uncheckedStarts?.has(span.indexes[0]))
                .map((span, index) => (
                  <rect
                    key={`unchecked-${index}`}
                    x={span.minX}
                    y={span.minY}
                    width={span.maxX - span.minX}
                    height={span.maxY - span.minY}
                    className="text-gray-500"
                    stroke="currentColor"
                    fill="none"
                    strokeWidth={Math.max(1.5, natural.width / 500)}
                    strokeDasharray={`${Math.max(4, natural.width / 250)} ${Math.max(3, natural.width / 350)}`}
                  />
                ))}
              {hoveredSpan && (
                <text
                  x={hoveredSpan.minX}
                  y={Math.max(Math.max(10, natural.width / 90), hoveredSpan.minY - Math.max(10, natural.width / 90) * 0.4)}
                  fontSize={Math.max(10, natural.width / 90)}
                  fontWeight={600}
                  className={`${hoveredSpan.colorClass} fill-current`}
                  stroke="white"
                  strokeWidth={Math.max(10, natural.width / 90) / 5}
                  style={{ paintOrder: "stroke", pointerEvents: "none" }}
                >
                  <tspan>{hoveredSpan.name}</tspan>
                  {hoveredText && <tspan fontWeight={400}> · {hoveredText}</tspan>}
                </text>
              )}
              {recommendedSpans.map((span, index) => {
                const isActive = span.indexes.some((tokenIndex) => activeIndexSet.has(tokenIndex));
                const fontSize = Math.max(10, natural.width / 90);

                return (
                  <g key={index}>
                    <rect
                      x={span.minX - 2}
                      y={span.minY - 2}
                      width={span.maxX - span.minX + 4}
                      height={span.maxY - span.minY + 4}
                      className={isActive ? "fill-orange-500/15 stroke-orange-600" : "stroke-orange-400"}
                      fill={isActive ? undefined : "none"}
                      strokeWidth={isActive ? Math.max(3, natural.width / 400) : Math.max(2, natural.width / 600)}
                      strokeDasharray={isActive ? undefined : `${natural.width / 150} ${natural.width / 250}`}
                    />
                    {isActive && (
                      <text
                        x={span.minX}
                        y={Math.max(fontSize, span.minY - fontSize * 0.4)}
                        fontSize={fontSize}
                        fontWeight={700}
                        className="fill-orange-700"
                        stroke="white"
                        strokeWidth={fontSize / 5}
                        style={{ paintOrder: "stroke" }}
                      >
                        {span.name === "O" ? "Retirer" : span.name}
                      </text>
                    )}
                  </g>
                );
              })}
              {flashIndexes.map((tokenIndex) =>
                tokenBoxes[tokenIndex] ? (
                  <rect
                    key={`flash-${tokenIndex}`}
                    x={tokenBoxes[tokenIndex].minX - 3}
                    y={tokenBoxes[tokenIndex].minY - 3}
                    width={tokenBoxes[tokenIndex].maxX - tokenBoxes[tokenIndex].minX + 6}
                    height={tokenBoxes[tokenIndex].maxY - tokenBoxes[tokenIndex].minY + 6}
                    className="stroke-primary-500"
                    fill="none"
                    strokeWidth={Math.max(2, natural.width / 500)}
                  />
                ) : null,
              )}
              {manualBoxes.map((box) => {
                const fontSize = Math.max(10, natural.width / 90);
                return (
                  <g key={`manual-${box.index}`}>
                    <rect
                      x={box.minX}
                      y={box.minY}
                      width={box.maxX - box.minX}
                      height={box.maxY - box.minY}
                      className={`${box.colorClass} fill-current`}
                      fillOpacity="0.18"
                      stroke="currentColor"
                      strokeWidth={Math.max(2, natural.width / 500)}
                      strokeDasharray={box.unchecked ? `${Math.max(4, natural.width / 250)} ${Math.max(3, natural.width / 350)}` : undefined}
                    />
                    {box.struck && (
                      <line
                        x1={box.minX}
                        y1={(box.minY + box.maxY) / 2}
                        x2={box.maxX}
                        y2={(box.minY + box.maxY) / 2}
                        className="text-amber-600"
                        stroke="currentColor"
                        strokeWidth={Math.max(2, natural.width / 350)}
                      />
                    )}
                    <text
                      x={box.minX}
                      y={Math.max(fontSize, box.minY - fontSize * 0.4)}
                      fontSize={fontSize}
                      fontWeight={700}
                      className={`${box.colorClass} fill-current`}
                      stroke="white"
                      strokeWidth={fontSize / 5}
                      style={{ paintOrder: "stroke" }}
                    >
                      {box.label}: {box.text}
                    </text>
                    <circle
                      cx={box.maxX}
                      cy={box.minY}
                      r={fontSize * 0.7}
                      className="fill-red-600"
                      style={{ pointerEvents: "auto", cursor: "pointer" }}
                      onMouseDown={(event) => {
                        event.stopPropagation();
                        onRemoveManual(box.index);
                      }}
                    />
                    <text x={box.maxX} y={box.minY + fontSize * 0.35} fontSize={fontSize} textAnchor="middle" fill="white" style={{ pointerEvents: "none" }}>
                      ×
                    </text>
                  </g>
                );
              })}
              {selection && (
                <rect
                  x={Math.min(selection.startX, selection.endX)}
                  y={Math.min(selection.startY, selection.endY)}
                  width={Math.abs(selection.endX - selection.startX)}
                  height={Math.abs(selection.endY - selection.startY)}
                  className="fill-primary-500/15 stroke-primary-500"
                  strokeWidth={Math.max(1, natural.width / 1200)}
                />
              )}
            </svg>
          )}
        </div>
      </div>

      <div className="absolute bottom-4 right-4 flex items-center rounded-lg border border-gray-200 bg-white shadow-sm">
        <button type="button" aria-label="Zoom arrière" className="p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700" onClick={() => onZoomStep(1 / 1.25)}>
          <PiMinus className="h-3.5 w-3.5" />
        </button>
        <div className="w-12 text-center text-xs tabular-nums text-gray-600">{Math.round(zoom * 100)}%</div>
        <button type="button" aria-label="Zoom avant" className="p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700" onClick={() => onZoomStep(1.25)}>
          <PiPlus className="h-3.5 w-3.5" />
        </button>
        <div className="h-5 w-px bg-gray-200" />
        <button type="button" aria-label="Ajuster à la largeur" className="p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700" onClick={() => setZoom(1)}>
          <PiCornersOut className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default DocumentCanvas;
